import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { reportError } from 'utils/error'
import { buildErrorMessage } from 'utils'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useQuery } from 'react-query'

interface BestPerformingPlatformPost {
  caption: string
  comments_count: number
  created_at: string
  engagement_rate: number
  external_integration_id: number
  id: number
  image_description: string
  impressions: number
  like_count: number
  media_type: string
  media_url: string
  post_id: number
  timestamp: string
  topic: string
  total_interactions: number
  updated_at: string
}

interface Data {
  posts: BestPerformingPlatformPost[]
}

const fetchBestPerformingPosts = async ({
  workspaceId,
  externalIntegrationId,
}: {
  workspaceId: number
  externalIntegrationId: number
  items?: number
}) => {
  const response = await sendToApi<Data>(
    `/w/${workspaceId}/external_integrations/${externalIntegrationId}/best_performing_posts`,
    {
      method: 'GET',
    },
  )

  if (response.type !== SUCCESS_RESPONSE_TYPE) {
    reportError('Failed to fetch best performing posts', {
      metaData: {
        response: response.data,
      },
    })
    throw new Error(
      buildErrorMessage(response.data.errors) ||
        'An error occurred! Our team has been notified and we are looking into it.',
    )
  }
  return response.data
}

export const useBestPerformingPlatformPosts = ({
  externalIntegrationId,
  items,
}: {
  externalIntegrationId: number
  items?: number
}) => {
  const workspaceId = useCurrentWorkspaceId()

  return useQuery<Data, Error>(
    ['workspaces', workspaceId, 'external_integrations', externalIntegrationId, 'best_performing_posts'],
    () => fetchBestPerformingPosts({ workspaceId, externalIntegrationId, items }),
    {
      enabled: !!externalIntegrationId,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    },
  )
}
